import { PAGE_KEY } from './constants';
import type { TPTTranslations } from './types';

export const en: TPTTranslations = {
  [PAGE_KEY]: {
    PAGE_TITLE: 'Community',
    PAGE_DESCRIPTION:
      'Fosters communication and a sense of community in your company with a channel where you can publish content of interest.',

    GLOBAL_ERROR_MESSAGE: 'Unable to execute the action. Please try again.',
    GLOBAL_CANCEL: 'Cancel',

    CHANNEL_INFO_ERROR_TITLE: 'An error occurred',
    CHANNEL_INFO_ERROR_DESCRIPTION:
      'An error occurred while loading the channel information.',

    PAGE_BREADCRUMB_COMMUNITY: 'Community',
    PAGE_BREADCRUMB_HOME: 'Home',
    PAGE_BREADCRUMB_CONNECTION: 'Connection',
    PAGE_BREADCRUMB_CHANNELS: 'My channels',

    BANNER_PUBLIC_LABEL: 'Public',
    BANNER_PUBLIC_TOOLTIP: 'Everyone can see the posts.',
    BANNER_PRIVATE_LABEL: 'Private',
    BANNER_PRIVATE_TOOLTIP: 'Only admins can see the posts.',

    TABS_FEED_LABEL: 'Feed',
    TABS_INFORMATION_LABEL: 'Information',
    TABS_SCHEDULED_LABEL: 'Scheduled',
    CONFIGURATION_BUTTON_LABEL: 'Configuration',

    CHANNEL_INFORMATION_TITLE: 'Channel information',
    CHANNEL_INFORMATION_NAME: 'Channel name',
    CHANNEL_INFORMATION_DESCRIPTION: 'Description',
    CHANNEL_INFORMATION_PERMISSIONS: 'Permissions',
    CHANNEL_INFORMATION_PUBLIC: 'Public channel.',
    CHANNEL_INFORMATION_PRIVATE: 'Private channel.',
    CHANNEL_INFORMATION_ADMIN_PUBLISH: 'Only admins can publish.',
    CHANNEL_INFORMATION_DESCRIPTION_CONTENT:
      'Welcome to the space where we will share announcements with the entire company.',

    // Post interactions
    POST_VIEW_COMMENTS: 'View {{count}} comment',
    POST_VIEW_COMMENTS_plural: 'View the {{count}} comments',
    POST_REACT_BUTTON: 'React',
    POST_COMMENT_BUTTON: 'Comment',
    POST_VIEW_CREATED_AT: 'Created: {{date}}',
    POST_VIEW_SHOW_MORE: 'Show more',
    POST_VIEW_SHOW_LESS: 'Show less',
    POST_VIEW_UPDATED_AT: 'Updated: {{date}}',
    COMMENT_INPUT_DISABLED_PLACEHOLDER: 'Comments are disabled',

    // Error view
    ERROR_TITLE: 'An error occurred',
    ERROR_DESCRIPTION: 'Click the button to try again.',
    ERROR_RETRY_BUTTON: 'Try again',

    // Empty view
    EMPTY_TITLE: 'No posts yet',
    EMPTY_DESCRIPTION:
      'Make your first post to generate interactions between your collaborators.',
    EMPTY_DESCRIPTION_END_USER: 'Soon you will know the news of your company.',
    EMPTY_CREATE_BUTTON: 'Create post',
    EMPTY_NO_POSTS_ALT: 'No posts',

    COMMENT_INPUT_PLACEHOLDER: 'Write a comment',
    COMMENT_INPUT_BUTTON: 'Comment',
    COMMENT_SUCCESS_MESSAGE: 'Comment published successfully.',
    COMMENT_INPUT_REQUIRED_ERROR: 'The comment is required',
    COMMENT_INPUT_MAX_LENGTH_ERROR:
      'The comment must be less than 500 characters',

    COMMENT_LIKE_BUTTON: 'Like',
    SHOW_MORE_COMMENTS: 'Show more comments',

    POST_EDIT_COMMENT_BUTTON: 'Edit',
    POST_DELETE_COMMENT_BUTTON: 'Delete',

    POST_REACTION_LIKE: 'Like',
    POST_REACTION_LOVE: 'Love',
    POST_REACTION_INTERESTED: 'Interested',

    DELETE_COMMENT_TITLE_OWN: 'Do you want to delete your comment?',
    DELETE_COMMENT_TITLE_OTHER:
      'Do you want to delete the comment of {{userName}}?',
    DELETE_COMMENT_SUBTITLE:
      'By confirming, you will not be able to revert this action.',
    DELETE_COMMENT_CTA_CANCEL: 'Cancel',
    DELETE_COMMENT_CTA_CONFIRM: 'Yes, delete',

    POSTS_FEED_RECENT_POSTS: 'View recent posts',
    POSTS_FEED_NEW_POSTS: 'There are new posts',

    REACTION_TABLE_USER_HEADER: 'Name',

    // Channels view
    CHANNELS_VIEW_TRIGGER_BUTTON: 'View channels',
    CHANNELS_VIEW_TITLE: 'Channels',
    CHANNELS_VIEW_CHANNEL_SEARCH_PLACEHOLDER: 'Search channel',
    CHANNELS_VIEW_CHANNEL_CREATE_BUTTON: 'Create channel',
    CHANNELS_VIEW_CHANNEL_DISCOVER_BUTTON: 'Discover channels',
    CHANNELS_VIEW_CHANNEL_MAIN_CHANNEL: 'Main',
    CHANNELS_VIEW_CHANNEL_OTHER_CHANNELS: 'More channels',
    CHANNELS_VIEW_CHANNEL_OTHER_CHANNELS_END_USER: 'You are a member of',
    CHANNELS_VIEW_ERROR_TITLE: 'An error occurred',
    CHANNELS_VIEW_ERROR_DESCRIPTION: 'Click the button to try again.',
    CHANNELS_VIEW_EMPTY_TITLE: 'No results found for',
    CHANNELS_VIEW_EMPTY_DESCRIPTION: 'Try again with a different term.',

    // Leave Channel
    LEAVE_CHANNEL_BUTTON: 'Leave channel',
    LEAVE_CHANNEL_TITLE: 'Do you want to leave the channel {{name}}?',
    LEAVE_CHANNEL_DESCRIPTION:
      'Your messages will remain visible in the channel until an administrator removes them. You can rejoin if you still have the necessary permissions to access the channel.',
    LEAVE_CHANNEL_CTA: 'Yes, leave it',
    CHANNEL_LEAVE_SUCCESS: 'You have left the channel {{name}} correctly.',
    CHANNEL_LEAVE_SUCCESS_DESCRIPTION:
      'You can rejoin if you still have the necessary permissions to access the channel.',

    UPLOAD_FILE_INPUT_BUTTON_LABEL: 'Upload image',
    UPLOAD_FILE_INPUT_LABEL_DESCRIPTION:
      'Format: JPG, PNG, GIF, MP4 or MOV. Size: 1280 x 720 px. Max: 5 MB image and 1 GB video.',
    UPLOAD_FILE_INPUT_FORMAT_LABEL: 'Format and size instructions',

    CONFIRM_DIALOG_TITLE: 'Do you want to leave and discard the post?',
    CONFIRM_DIALOG_TITLE_EDIT:
      'Do you want to leave without saving your changes?',
    CONFIRM_DIALOG_DESCRIPTION: 'The information you entered will be lost.',
    CONFIRM_DIALOG_DESCRIPTION_EDIT: 'The modifications you made will be lost.',
    CONFIRM_DIALOG_CANCEL_TEXT: 'Cancel',
    CONFIRM_DIALOG_CONFIRM_TEXT: 'Yes, leave',

    CREATE_NEW_POST_INPUT_BUTTON_LABEL:
      'What would you like to communicate today?',
    CREATE_NEW_POST_INPUT_TITLE: 'Create post',
    CREATE_NEW_POST_INPUT_TITLE_EDIT: 'Edit post',
    CREATE_NEW_POST_INPUT_DESCRIPTION:
      'Write a message for your community. You can add images, videos, and links.',
    CREATE_NEW_POST_INPUT_CONFIRM_BUTTON_LABEL: 'Publish',
    CREATE_NEW_POST_INPUT_SCHEDULE_BUTTON_LABEL: 'Schedule',
    CREATE_NEW_POST_INPUT_SAVE_BUTTON_LABEL: 'Save',
    CREATE_NEW_POST_INPUT_PUBLISH_NOW_BUTTON_LABEL: 'Publish now',
    CREATE_NEW_POST_INPUT_PLACEHOLDER: 'Space to write text',
    CREATE_NEW_POST_INPUT_REACH_LABEL: 'Reach: {{count}} person',
    CREATE_NEW_POST_INPUT_REACH_LABEL_plural: 'Reach: {{count}} people',

    CREATE_NEW_POST_INPUT_SUCCESS_MESSAGE: 'Message published successfully.',
    CREATE_NEW_POST_INPUT_SCHEDULED_SUCCESS_MESSAGE:
      'Post scheduled successfully.',
    UPDATE_POST_INPUT_SUCCESS_MESSAGE: 'Changes saved successfully.',
    DELETE_POST_INPUT_SUCCESS_MESSAGE: 'Post deleted successfully.',

    POST_DELETE_TITLE: 'Do you want to delete the post?',
    POST_DELETE_BUTTON: 'Delete',
    POST_EDIT_BUTTON: 'Edit',
    POST_DELETE_DESCRIPTION:
      'This action is irreversible. If the post has comments, they will also be permanently deleted.',
    POST_DELETE_CONFIRM_BUTTON: 'Yes, delete',
    POST_DELETE_SUCCESS_MESSAGE: 'Post deleted successfully.',

    // Edit Channel
    EDIT_CHANNEL_BUTTON: 'Edit configuration',

    // Moderators selector
    STEP_3_ASSIGNED_USERS_TITLE: 'Assigned users',
    STEP_3_ASSIGNED_USERS_PLACEHOLDER: 'Select users',
    STEP_3_ASSIGNED_USERS_SEARCH_PLACEHOLDER: 'Search',
    STEP_3_ASSIGNED_USERS_EMPTY: 'No users available.',
    STEP_3_ASSIGNED_USERS_REMOVE_LABEL: 'Remove user',
    STEP_3_MODERATORS_LIMIT_REACHED:
      "You've reached the limit of 100 moderators.",

    // Delete Channel
    DELETE_CHANNEL_BUTTON: 'Delete',
    DELETE_CHANNEL_TITLE: 'Do you want to delete the channel {{name}}?',
    DELETE_CHANNEL_DESCRIPTION:
      'This action is irreversible and you will lose all posts, comments and metrics. Write “DELETE” to confirm the action.',
    DELETE_CHANNEL_CONFIRMATION: 'DELETE',
    DELETE_CHANNEL_CTA: 'Yes, delete',
    CHANNEL_DELETE_SUCCESS: 'The deletion was successful.',
    CHANNEL_DELETE_SUCCESS_DESCRIPTION:
      'Remember that you cannot reverse this action.',
    CHANNEL_DELETE_ERROR: 'Unable to complete the deletion ',
    CHANNEL_DELETE_ERROR_DESCRIPTION: 'Please try again later.',

    // Convert to main channel
    CONVERT_TO_MAIN_CHANNEL_BUTTON: 'Convert to main channel',
    CONVERT_TO_MAIN_CHANNEL_TITLE:
      'Do you want to convert this channel to main?',
    CONVERT_TO_MAIN_CHANNEL_DESCRIPTION:
      'Before confirming, consider these considerations about the main channel:',
    CONVERT_TO_MAIN_CHANNEL_MESSAGE_1:
      'The main channel is the one that first appears when entering “Community”.',
    CONVERT_TO_MAIN_CHANNEL_MESSAGE_2:
      'You will not be able to edit the “Join permissions” configuration, since the main channel must always be public, automatically include all company members, and cannot be abandoned.',
    CONVERT_TO_MAIN_CHANNEL_MESSAGE_3:
      'If this channel has users who have left it, they will be automatically reincorporated when setting it as the main channel.',
    CONVERT_TO_MAIN_CHANNEL_MESSAGE_4:
      'You cannot delete the main channel until assigning another in its place.',
    CONVERT_TO_MAIN_CHANNEL_CONFIRMATION:
      'I confirm that I understand the implications and want to continue with the change.',
    CONVERT_TO_MAIN_CHANNEL_CTA: 'Yes, convert',
    CHANNEL_CONVERT_TO_MAIN_SUCCESS:
      'The channel was successfully set as the main channel.',
    CHANNEL_CONVERT_TO_MAIN_SUCCESS_DESCRIPTION:
      'All active and pending users will automatically join this channel.',

    // Upload image or video dialog
    UPLOAD_IMAGE_OR_VIDEO_BUTTON_LABEL: 'Image or video',
    UPLOAD_IMAGE_OR_VIDEO_DIALOG_TITLE: 'Upload image or video',
    UPLOAD_IMAGE_OR_VIDEO_ALT_TEXT_LABEL:
      'Alternative description for accessibility',
    UPLOAD_IMAGE_OR_VIDEO_ALT_TEXT_DESCRIPTION:
      'Screen readers read this for people with visual disabilities.',
    UPLOAD_IMAGE_OR_VIDEO_ALT_TEXT_PLACEHOLDER:
      'Brief description of the image or video.',
    UPLOAD_IMAGE_OR_VIDEO_DIALOG_SUBMIT_BUTTON: 'Insert',
    ALTERNATIVE_TEXT_ERROR_MESSAGE:
      'Only letters, numbers, spaces, hyphens, parentheses, periods, and commas are allowed.',

    CREATE_NEW_POST_INPUT_NOTIFY_LABEL: 'Notify',
    CREATE_NEW_POST_INPUT_NOTIFY_TOOLTIP:
      'Activate the option if you want to notify this publication to your audience by email.',
    SCHEDULE_FOR_LATER_LABEL: 'Schedule for later',
    SCHEDULE_FOR_LATER_DESCRIPTION:
      'Set a date and time for the publication to be automatically published.',
    SCHEDULE_DATE_LABEL: 'Date',
    SCHEDULE_DATE_PLACEHOLDER: 'Select a date',
    SCHEDULE_TIME_LABEL: 'Time',
    SCHEDULE_TIME_PLACEHOLDER: 'Select time',
    SCHEDULE_TIMEZONE_LABEL: 'Time zone',
    SCHEDULED_EMPTY_TITLE: 'No scheduled posts yet',
    SCHEDULED_EMPTY_DESCRIPTION:
      'Messages scheduled to be published at a future date and time will appear here.',
    SCHEDULED_CREATE_BUTTON: 'Create post',
    SCHEDULED_TABLE_DESCRIPTION:
      'Admins and moderators can view all channel posts.',
    SCHEDULED_SEARCH_PLACEHOLDER: 'Search',
    SCHEDULED_COLUMN_POST: 'Scheduled post',
    SCHEDULED_COLUMN_PRE_CREATION_DATE: 'Pre-creation date',
    SCHEDULED_COLUMN_SCHEDULED_DATE: 'Scheduled date',
    SCHEDULED_COLUMN_ACTIONS: 'Actions',
    SCHEDULED_PRE_CREATION_TOOLTIP:
      'This date will not appear in the message when published, but the original author will.',
    SCHEDULED_USER_DEACTIVATED_TOOLTIP:
      'User deleted or deactivated from the platform',
    SCHEDULED_ACTION_OPEN_ACTIONS: 'Open post actions',
    SCHEDULED_ACTION_PREVIEW: 'Preview',
    SCHEDULED_ACTION_EDIT: 'Edit',
    SCHEDULED_ACTION_HISTORY: 'View change history',
    SCHEDULED_ACTION_PUBLISH_NOW: 'Publish now',
    SCHEDULED_ACTION_DELETE: 'Delete',
    SCHEDULED_SEARCH_EMPTY_TITLE: 'No results found for',
    SCHEDULED_SEARCH_EMPTY_DESCRIPTION: 'Try again with a different term.',
    SCHEDULED_SORT_NEWEST: 'Newest',
    SCHEDULED_SORT_OLDEST: 'Oldest',
    SCHEDULED_BY_AUTHOR: 'By: {{name}}',
    SCHEDULED_AT_CONNECTOR: 'at',
    SCHEDULED_BULK_DELETE_TOOLTIP: 'Delete',
    SCHEDULED_PUBLISH_NOW_DIALOG_TITLE:
      'Do you want to publish the message now?',
    SCHEDULED_PUBLISH_NOW_DIALOG_DESCRIPTION:
      'The message will no longer be scheduled and will be displayed immediately on the channel wall.',
    SCHEDULED_PUBLISH_NOW_DIALOG_CONFIRM: 'Yes, publish',
    SCHEDULED_PUBLISH_NOW_SUCCESS: 'Message published successfully.',
    SCHEDULED_DELETE_DIALOG_TITLE: 'Do you want to delete the post?',
    SCHEDULED_DELETE_DIALOG_DESCRIPTION:
      'This action is irreversible and you will lose the content of the post.',
    SCHEDULED_DELETE_DIALOG_CONFIRM: 'Yes, delete',
    SCHEDULED_DELETE_SUCCESS: 'The deletion was completed successfully.',
    SCHEDULED_DELETE_SUCCESS_DESCRIPTION:
      'Remember that you cannot reverse this action.',
    SCHEDULED_DELETE_ERROR: 'Unable to complete the deletion',
    SCHEDULED_DELETE_ERROR_DESCRIPTION: 'Please try again later.',
    SCHEDULED_BULK_DELETE_DIALOG_TITLE: 'Do you want to delete {{count}} post?',
    SCHEDULED_BULK_DELETE_DIALOG_TITLE_MULTIPLE:
      'Do you want to delete {{count}} posts?',
    SCHEDULED_BULK_DELETE_DIALOG_DESCRIPTION:
      'This action is irreversible and you will lose the content of the messages. Write "DELETE" to confirm the action.',
    SCHEDULED_BULK_DELETE_DIALOG_CONFIRMATION: 'DELETE',
    SCHEDULED_BULK_DELETE_DIALOG_CONFIRM: 'Yes, delete',
    SCHEDULED_BULK_DELETE_DIALOG_CONFIRM_INPUT_LABEL:
      'Type {{word}} to confirm deletion',
    SCHEDULE_TIME_INVALID: 'Invalid time, please select a future time.',
    SCHEDULED_PREVIEW_DIALOG_TITLE: 'Post preview',
    SCHEDULED_PREVIEW_CREATION_DATE: 'Created: {{date}}',
    SCHEDULED_PREVIEW_SCHEDULED_DATE: 'Scheduled: {{date}}',
    SCHEDULED_PREVIEW_ONLY_PREVIEW_TEXT: 'Less than a minute ago',
    SCHEDULED_PREVIEW_SHOW_MORE: 'Show more',
    SCHEDULED_PREVIEW_SHOW_LESS: 'Show less',
  },
};
